import type * as lazyIndex from './lazyIndex';
import { LazyAction, LazyImport, LazyModule } from 'owa-bundling-light';

const lazyModule: LazyModule<typeof lazyIndex> = new LazyModule<typeof lazyIndex>(
    () => import(/* webpackChunkName: "Notification"*/ './lazyIndex'),
    {
        name: 'Notification',
    }
);

// Lazy functions for notification consumers
export const lazySubscribe: LazyAction<typeof lazyIndex.subscribe, typeof lazyModule> =
    new LazyAction(lazyModule, m => m.subscribe);
export const lazyUnsubscribe: LazyAction<typeof lazyIndex.unsubscribe, typeof lazyModule> =
    new LazyAction(lazyModule, m => m.unsubscribe);
export const lazyRetryAllDisconnectionsWithMinimumReconnectInterval: LazyAction<
    typeof lazyIndex.NotificationManager.retryAllDisconnectionsWithMinimumReconnectInterval,
    typeof lazyModule
> = new LazyAction(
    lazyModule,
    m => m.NotificationManager.retryAllDisconnectionsWithMinimumReconnectInterval
);
export const lazyRetryDisconnection: LazyAction<
    typeof lazyIndex.NotificationManager.retryDisconnection,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.NotificationManager.retryDisconnection);
export const lazySetup: LazyAction<typeof lazyIndex.setup, typeof lazyModule> = new LazyAction(
    lazyModule,
    m => m.setup
);
export const lazyHandleNotification: LazyAction<
    typeof lazyIndex.handleNotification,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.handleNotification);
export const lazyStopAllConnections: LazyAction<
    typeof lazyIndex.NotificationManager.stopAllConnections,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.NotificationManager.stopAllConnections);

// Lazy functions for channel initialization
export const lazyInitializeNotificationManager: LazyAction<
    typeof lazyIndex.initializeNotificationManager,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.initializeNotificationManager);

export const lazyLogNotificationConnectionStatus: LazyAction<
    typeof lazyIndex.NotificationManager.getConnectionStatusesForAllAccounts,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.NotificationManager.getConnectionStatusesForAllAccounts);
export const lazyGetNotificationConnectionStatus: LazyAction<
    typeof lazyIndex.NotificationManager.getConnectionStatusesObjectForAllAccounts,
    typeof lazyModule
> = new LazyAction(
    lazyModule,
    m => m.NotificationManager.getConnectionStatusesObjectForAllAccounts
);
export const lazyGetNotificationEmitter: LazyImport<
    typeof lazyIndex.getNotificationEmitter,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.getNotificationEmitter);

export const lazyInitializeNotifications: LazyAction<
    typeof lazyIndex.initializeNotifications,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.initializeNotifications);

export type {
    default as NotificationSubscription,
    MailboxNotificationSubscription,
    AllMailboxNotificationSubscription,
} from './schema/NotificationSubscription';
export type { SubscriptionParameters } from './schema/SubscriptionParameters';
export { default as NotificationEventType } from './schema/NotificationEventType';
export type { default as NotificationCallback } from './schema/NotificationCallback';
export { SubscriptionStatus, type SubscriptionState } from './schema/SubscriptionTrackerState';
export type { NotificationPayloadWithDiagnostics } from './schema/NotificationPayloadWithDiagnostics';
export { ConnectionState } from './schema/ConnectionState';
export type { ChannelDataEvent } from './schema/ChannelDataEvent';
export { ConnectionType } from './schema/ConnectionType';
export { NotificationType } from './schema/NotificationType';
